/**
 * TelegramProvider — initializes @telegram-apps/sdk and exposes user identity.
 * Mirrors sagwa-box pattern. KO-first language resolution.
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
  type ReactElement,
} from 'react'
import { getInitDataRaw } from '../api/apiClient'

export type TelegramUser = {
  readonly id: number
  readonly firstName: string
  readonly lastName: string | null
  readonly username: string | null
  readonly languageCode: string | null
  readonly photoUrl: string | null
}

export type TelegramContextValue = {
  readonly user: TelegramUser | null
  readonly isReady: boolean
  readonly isDev: boolean
  /** Raw Telegram initData string for `Authorization: tma`, or null in dev. */
  readonly initDataRaw: string | null
}

const TelegramContext = createContext<TelegramContextValue | null>(null)

/** Resolve Telegram WebApp user from window.Telegram.WebApp */
function resolveTgUser(): TelegramUser | null {
  try {
    const tg = window.Telegram?.WebApp
    if (!tg) return null
    const u = tg.initDataUnsafe?.user
    if (!u?.id) return null
    return {
      id: u.id,
      firstName: u.first_name ?? '',
      lastName: u.last_name ?? null,
      username: u.username ?? null,
      languageCode: u.language_code ?? null,
      photoUrl: u.photo_url ?? null,
    }
  } catch {
    return null
  }
}

/**
 * Local-dev identity fallback. In a plain browser during `vite dev` there is no
 * Telegram WebApp, so synthesize the user from `VITE_DEV_USER` ("<id>:<name>")
 * — the same id the match handshake sends as `X-Dev-User`, so the client's
 * self-id matches its seat and "my turn" detection works. Null in production.
 */
function resolveDevUser(): TelegramUser | null {
  const raw = import.meta.env.VITE_DEV_USER as string | undefined
  if (!raw) return null
  const [idPart, namePart] = raw.split(':')
  const id = Number.parseInt(idPart ?? '', 10)
  if (!Number.isFinite(id) || id <= 0) return null
  return {
    id,
    firstName: namePart ?? 'Dev',
    lastName: null,
    username: null,
    languageCode: 'ko',
    photoUrl: null,
  }
}

export function TelegramProvider({ children }: PropsWithChildren): ReactElement {
  const isDev = import.meta.env.DEV
  const [isReady, setIsReady] = useState(false)
  const [user, setUser] = useState<TelegramUser | null>(null)
  const [initDataRaw, setInitDataRaw] = useState<string | null>(null)

  useEffect(() => {
    try {
      const tg = window.Telegram?.WebApp
      if (tg) {
        tg.ready()
        tg.expand()
      }
    } catch {
      // non-fatal in dev
    }
    setUser(resolveTgUser() ?? (isDev ? resolveDevUser() : null))
    setInitDataRaw(getInitDataRaw())
    setIsReady(true)
  }, [isDev])

  return (
    <TelegramContext.Provider value={{ user, isReady, isDev, initDataRaw }}>
      {children}
    </TelegramContext.Provider>
  )
}

export function useTelegram(): TelegramContextValue {
  const ctx = useContext(TelegramContext)
  if (!ctx) throw new Error('useTelegram must be inside <TelegramProvider>')
  return ctx
}
